import * as React from 'react';
import { Redirect, Switch, useLocation } from 'react-router-dom';
import * as fp from 'lodash/fp';

import {
  AppRoute,
  withFullHeight,
  withRouteData,
} from 'app/components/app-router';
import {
  DataUserCodeOfConduct,
  DuccSignatureState,
} from 'app/components/data-user-code-of-conduct';
import { withRoutingSpinner } from 'app/components/with-routing-spinner';
import { BreadcrumbType } from 'app/lab/components/breadcrumb-type';
import { DataAccessRequirements } from 'app/pages/access/data-access-requirements';
import { AdminBannerTable } from 'app/pages/admin/admin-banner-table';
import { AdminEgressAudit } from 'app/pages/admin/admin-egress-audit';
import { AdminEgressEvents } from 'app/pages/admin/admin-egress-events';
import { WorkspaceAudit } from 'app/pages/admin/admin-workspace-audit';
import { BatchSyncUserAccess } from 'app/pages/admin/batch-sync-user-access';
import { AdminInstitution } from 'app/pages/admin/institution/admin-institution';
import { AdminInstitutionEdit } from 'app/pages/admin/institution/admin-institution-edit';
import { AdminUserProfile } from 'app/pages/admin/user/admin-user-profile';
import { AdminUserTable } from 'app/pages/admin/user/admin-user-table';
import { UserAudit } from 'app/pages/admin/user-audit';
import { AdminVwbDataCollections } from 'app/pages/admin/vwb/admin-vwb-data-collections';
import { AdminVwbGroups } from 'app/pages/admin/vwb/admin-vwb-groups';
import { AdminVwbWorkspace } from 'app/pages/admin/vwb/admin-vwb-workspace';
import { AdminVwbWorkspaceSearch } from 'app/pages/admin/vwb/admin-vwb-workspace-search';
import { AdminWorkspace } from 'app/pages/admin/workspace/admin-workspace';
import { AdminWorkspaceSearch } from 'app/pages/admin/workspace/admin-workspace-search';
import { AdminWorkspacesWaitingForRetrieval } from 'app/pages/admin/workspace/admin-workspaces-waiting-for-retrieval';
import { DemographicSurvey } from 'app/pages/demographic-survey';
import { Homepage } from 'app/pages/homepage/homepage';
import { ProfileComponent } from 'app/pages/profile/profile-component';
import { WorkspaceList } from 'app/pages/workspace/workspace-list';
import { WorkspaceRecovery } from 'app/pages/workspace/workspace-recovery';
import {
  DARPageMode,
  DATA_ACCESS_REQUIREMENTS_PATH,
  RAS_CALLBACK_PATH,
} from 'app/utils/access-utils';
import { AuthorityGuardedAction } from 'app/utils/authorities';
import { DEMOGRAPHIC_SURVEY_V2_PATH } from 'app/utils/constants';
import { currentWorkspaceStore } from 'app/utils/navigation';

import {
  authorityGuard,
  getAccessModuleGuard,
  restrictDemographicSurvey,
} from './guards';

const AdminBannerPage = fp.flow(
  withRouteData,
  withRoutingSpinner
)(AdminBannerTable);
const AdminEgressAuditPage = fp.flow(
  withRouteData,
  withRoutingSpinner
)(AdminEgressAudit);
const AdminEgressEventsPage = fp.flow(
  withRouteData,
  withRoutingSpinner
)(AdminEgressEvents);
const BatchSyncUserAccessPage = fp.flow(
  withRouteData,
  withRoutingSpinner
)(BatchSyncUserAccess);
const DataAccessRequirementsPage = fp.flow(
  withRouteData,
  withRoutingSpinner
)(DataAccessRequirements);
const DataUserCodeOfConductPage = fp.flow(
  withRouteData,
  withFullHeight,
  withRoutingSpinner
)(DataUserCodeOfConduct);
const DemographicSurveyPage = fp.flow(
  withRouteData,
  withRoutingSpinner
)(DemographicSurvey);
const HomepagePage = fp.flow(withRouteData, withRoutingSpinner)(Homepage);
const InstitutionAdminPage = fp.flow(
  withRouteData,
  withRoutingSpinner
)(AdminInstitution);
const InstitutionEditAdminPage = fp.flow(
  withRouteData,
  withRoutingSpinner
)(AdminInstitutionEdit);
const ProfilePage = fp.flow(
  withRouteData,
  withRoutingSpinner
)(ProfileComponent);
const UserAdminProfilePage = fp.flow(
  withRouteData,
  withRoutingSpinner
)(AdminUserProfile);
const UserAdminTablePage = fp.flow(
  withRouteData,
  withRoutingSpinner
)(AdminUserTable);
const UserAuditPage = fp.flow(withRouteData, withRoutingSpinner)(UserAudit);
const WorkspaceAdminPage = fp.flow(
  withRouteData,
  withRoutingSpinner
)(AdminWorkspace);
const VwbDataCollectionAdminPage = fp.flow(
  withRouteData,
  withRoutingSpinner
)(AdminVwbDataCollections);
const VwbGroupAdminPage = fp.flow(
  withRouteData,
  withRoutingSpinner
)(AdminVwbGroups);
const VwbWorkspaceAdminSearchPage = fp.flow(
  withRouteData,
  withRoutingSpinner
)(AdminVwbWorkspaceSearch);
const VwbWorkspaceAdminPage = fp.flow(
  withRouteData,
  withRoutingSpinner
)(AdminVwbWorkspace);
const WorkspaceAuditPage = fp.flow(
  withRouteData,
  withRoutingSpinner
)(WorkspaceAudit);
const WorkspaceListPage = fp.flow(
  withRouteData,
  withRoutingSpinner
)(WorkspaceList);
const WorkspaceSearchAdminPage = fp.flow(
  withRouteData,
  withRoutingSpinner
)(AdminWorkspaceSearch);
const AdminWorkspacesWaitingForRetrievalPage = fp.flow(
  withRouteData,
  withRoutingSpinner
)(AdminWorkspacesWaitingForRetrieval);
const WorkspaceRecoveryPage = fp.flow(
  withRouteData,
  withRoutingSpinner
)(WorkspaceRecovery);

export const SignedInRoutes = () => {
  const location = useLocation();
  const query = new URLSearchParams(location.search);
  const [pathName, setPathName] = React.useState(location.pathname);
  React.useEffect(() => {
    if (
      pathName.startsWith('/workspaces/') &&
      !location.pathname.startsWith('/workspaces/')
    ) {
      currentWorkspaceStore.next(null);
    }
    setPathName(location.pathname);
  }, [location]);

  return (
    <Switch>
      <AppRoute exact path='/' guards={[getAccessModuleGuard()]}>
        <HomepagePage routeData={{ title: 'Homepage' }} />
      </AppRoute>
      <AppRoute
        exact
        path='/admin/banner'
        guards={[authorityGuard(AuthorityGuardedAction.SERVICE_BANNER)]}
      >
        <AdminBannerPage
          routeData={{ title: 'Create Banner', minimizeChrome: true }}
        />
      </AppRoute>
      <AppRoute
        exact
        path='/admin/egress-events'
        guards={[authorityGuard(AuthorityGuardedAction.EGRESS_EVENTS)]}
      >
        <AdminEgressEventsPage
          routeData={{ title: 'Egress Events', minimizeChrome: true }}
        />
      </AppRoute>
      <AppRoute
        exact
        path='/admin/egress-events/:eventId'
        guards={[authorityGuard(AuthorityGuardedAction.EGRESS_EVENTS)]}
      >
        <AdminEgressAuditPage
          routeData={{ title: 'Egress Event Audit', minimizeChrome: true }}
        />
      </AppRoute>
      <AppRoute
        exact
        path='/admin/institution'
        guards={[authorityGuard(AuthorityGuardedAction.INSTITUTION_ADMIN)]}
      >
        <InstitutionAdminPage
          routeData={{ title: 'Institution Admin', minimizeChrome: true }}
        />
      </AppRoute>
      <AppRoute
        exact
        path='/admin/institution/add'
        guards={[authorityGuard(AuthorityGuardedAction.INSTITUTION_ADMIN)]}
      >
        <InstitutionEditAdminPage
          routeData={{ title: 'Institution Admin', minimizeChrome: true }}
        />
      </AppRoute>
      <AppRoute
        exact
        path='/admin/institution/edit/:institutionId'
        guards={[authorityGuard(AuthorityGuardedAction.INSTITUTION_ADMIN)]}
      >
        <InstitutionEditAdminPage
          routeData={{ title: 'Institution Admin', minimizeChrome: true }}
        />
      </AppRoute>
      <AppRoute exact path='/admin/user'>
        {' '}
        {/* included for backwards compatibility */}
        <Redirect to={'/admin/users'} />
      </AppRoute>
      <AppRoute
        exact
        path='/admin/users'
        guards={[authorityGuard(AuthorityGuardedAction.USER_ADMIN)]}
      >
        <UserAdminTablePage
          routeData={{ title: 'User Admin Table', minimizeChrome: true }}
        />
      </AppRoute>
      <AppRoute
        exact
        path='/admin/users/:usernameWithoutGsuiteDomain'
        guards={[authorityGuard(AuthorityGuardedAction.USER_ADMIN)]}
      >
        <UserAdminProfilePage
          routeData={{ title: 'User Profile Admin', minimizeChrome: true }}
        />
      </AppRoute>
      <AppRoute
        exact
        path='/admin/user-audit'
        guards={[authorityGuard(AuthorityGuardedAction.USER_AUDIT)]}
      >
        <UserAuditPage
          routeData={{ title: 'User Audit', minimizeChrome: true }}
        />
      </AppRoute>
      <AppRoute
        exact
        path='/admin/bulk-sync-user-access'
        guards={[authorityGuard(AuthorityGuardedAction.USER_AUDIT)]}
      >
        <BatchSyncUserAccessPage
          routeData={{ title: 'Bulk Sync User Access', minimizeChrome: true }}
        />
      </AppRoute>
      <AppRoute
        exact
        path='/admin/user-audit/:username'
        guards={[authorityGuard(AuthorityGuardedAction.USER_AUDIT)]}
      >
        <UserAuditPage
          routeData={{ title: 'User Audit', minimizeChrome: true }}
        />
      </AppRoute>
      <AppRoute
        exact
        path='/admin/workspaces'
        guards={[authorityGuard(AuthorityGuardedAction.WORKSPACE_ADMIN)]}
      >
        <WorkspaceSearchAdminPage
          routeData={{ title: 'Workspace Admin', minimizeChrome: true }}
        />
      </AppRoute>
      <AppRoute
        exact
        path='/admin/workspaces/waiting-for-retrieval'
        guards={[authorityGuard(AuthorityGuardedAction.WORKSPACE_ADMIN)]}
      >
        <AdminWorkspacesWaitingForRetrievalPage
          routeData={{
            title: 'Workspaces Waiting for Retrieval',
            minimizeChrome: true,
          }}
        />
      </AppRoute>
      <AppRoute
        exact
        path='/admin/workspaces/:ns'
        guards={[authorityGuard(AuthorityGuardedAction.WORKSPACE_ADMIN)]}
      >
        <WorkspaceAdminPage
          routeData={{ title: 'Workspace Admin', minimizeChrome: true }}
        />
      </AppRoute>
      <AppRoute
        exact
        path='/admin/vwb/workspaces'
        guards={[authorityGuard(AuthorityGuardedAction.WORKSPACE_ADMIN)]}
      >
        <VwbWorkspaceAdminSearchPage
          routeData={{ title: 'VWB Workspace Admin', minimizeChrome: true }}
        />
      </AppRoute>
      <AppRoute
        exact
        path='/admin/vwb/workspaces/:ufid'
        guards={[authorityGuard(AuthorityGuardedAction.WORKSPACE_ADMIN)]}
      >
        <VwbWorkspaceAdminPage
          routeData={{ title: 'VWB Workspace Admin', minimizeChrome: true }}
        />
      </AppRoute>
      <AppRoute
        exact
        path='/admin/vwb/groups'
        guards={[authorityGuard(AuthorityGuardedAction.WORKSPACE_ADMIN)]}
      >
        <VwbGroupAdminPage
          routeData={{ title: 'VWB Group Admin', minimizeChrome: true }}
        />
      </AppRoute>
      <AppRoute
        exact
        path='/admin/vwb/data-collections'
        guards={[authorityGuard(AuthorityGuardedAction.WORKSPACE_ADMIN)]}
      >
        <VwbDataCollectionAdminPage
          routeData={{
            title: 'VWB Data Collections',
            minimizeChrome: true,
          }}
        />
      </AppRoute>
      <AppRoute
        exact
        path='/admin/workspace-audit'
        guards={[authorityGuard(AuthorityGuardedAction.WORKSPACE_AUDIT)]}
      >
        <WorkspaceAuditPage
          routeData={{ title: 'Workspace Audit', minimizeChrome: true }}
        />
      </AppRoute>
      <AppRoute
        exact
        path='/admin/workspace-audit/:ns'
        guards={[authorityGuard(AuthorityGuardedAction.WORKSPACE_AUDIT)]}
      >
        <WorkspaceAuditPage
          routeData={{ title: 'Workspace Audit', minimizeChrome: true }}
        />
      </AppRoute>
      <AppRoute exact path={DATA_ACCESS_REQUIREMENTS_PATH}>
        <DataAccessRequirementsPage
          routeData={{
            title:
              query.get('pageMode') === DARPageMode.ANNUAL_RENEWAL
                ? 'Access Renewal'
                : 'Data Access Requirements',
          }}
        />
      </AppRoute>
      <AppRoute exact path='/data-code-of-conduct'>
        <DataUserCodeOfConductPage
          routeData={{
            title: 'Data User Code of Conduct',
            minimizeChrome: true,
          }}
          signatureState={DuccSignatureState.UNSIGNED}
        />
      </AppRoute>
      <AppRoute exact path='/signed-ducc' guards={[getAccessModuleGuard()]}>
        <DataUserCodeOfConductPage
          routeData={{
            title: 'Data User Code of Conduct (Signed)',
            minimizeChrome: true,
          }}
          signatureState={DuccSignatureState.SIGNED}
        />
      </AppRoute>
      <AppRoute exact path='/profile'>
        <ProfilePage routeData={{ title: 'Profile' }} />
      </AppRoute>
      <AppRoute exact path={RAS_CALLBACK_PATH}>
        <DataAccessRequirementsPage
          routeData={{ title: 'Data Access Requirements' }}
        />
      </AppRoute>
      <AppRoute exact path='/workspaces' guards={[getAccessModuleGuard()]}>
        <WorkspaceListPage
          routeData={{
            title: 'View Workspaces',
            breadcrumb: BreadcrumbType.Workspaces,
          }}
        />
      </AppRoute>
      <AppRoute
        exact
        path='/workspaces/:ns/recovery'
        guards={[getAccessModuleGuard()]}
      >
        <WorkspaceRecoveryPage
          routeData={{
            title: 'Workspace Recovery',
            breadcrumb: BreadcrumbType.Workspace,
          }}
        />
      </AppRoute>
      <AppRoute
        path={DEMOGRAPHIC_SURVEY_V2_PATH}
        exact
        guards={[restrictDemographicSurvey()]}
      >
        <DemographicSurveyPage
          routeData={{
            title: 'Demographic Survey',
          }}
        />
      </AppRoute>
      <AppRoute exact path='*'>
        <Redirect to={'/not-found'} />
      </AppRoute>
    </Switch>
  );
};
